import { useState, useContext, useEffect } from 'react';
import { authContext } from '../context/index.js';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import { User, Mail, Shield, Bell, Moon, Sun, Globe } from 'lucide-react';
import { toast } from 'react-toastify';

const Settings = () => {
  const { userData } = useContext(authContext);
  
  // Load saved preferences from localStorage
  const loadPreferences = () => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      theme: 'light',
      language: 'en',
      notifications: {
        email: true,
        push: false,
        courseUpdates: true,
        newEnrollments: true,
      },
      privacy: {
        showProfile: true,
        showProgress: true,
      }
    };
  };

  // Platform Preferences State
  const [preferences, setPreferences] = useState(loadPreferences);

  // Apply dark mode when theme changes
  useEffect(() => {
    if (preferences.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save to localStorage
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences.theme]);

  const handleThemeChange = (theme) => {
    setPreferences(prev => ({ ...prev, theme }));
    toast.success(`Theme changed to ${theme} mode`);
  };

  const handleLanguageChange = (e) => {
    setPreferences(prev => ({ ...prev, language: e.target.value }));
  };

  const handleNotificationToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key]
      }
    }));
  };

  const handlePrivacyToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [key]: !prev.privacy[key]
      }
    }));
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Settings</h1>
        <p className="text-secondary-600 dark:text-gray-300 mt-1">Manage your account and preferences</p>
      </div>

      {/* User Profile Section */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <User className="w-5 h-5" />
            <span>Profile Information</span>
          </Card.Title>
          <Card.Description>Your personal information and account details</Card.Description>
        </Card.Header>

        <div className="space-y-6">
          {/* Profile Avatar */}
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-2xl">
                {userData?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">{userData?.name}</h3>
              <p className="text-sm text-secondary-600 dark:text-gray-300 capitalize">{userData?.role}</p>
            </div>
          </div>

          {/* Profile Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-gray-200 mb-2">
                Full Name
              </label>
              <div className="flex items-center space-x-2 p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg border border-secondary-200 dark:border-gray-600">
                <User size={18} className="text-secondary-400 dark:text-gray-400" />
                <span className="text-secondary-900 dark:text-white">{userData?.name}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-gray-200 mb-2">
                Email Address
              </label>
              <div className="flex items-center space-x-2 p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg border border-secondary-200 dark:border-gray-600">
                <Mail size={18} className="text-secondary-400 dark:text-gray-400" />
                <span className="text-secondary-900 dark:text-white">{userData?.email}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-gray-200 mb-2">
                Account Type
              </label>
              <div className="flex items-center space-x-2 p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg border border-secondary-200 dark:border-gray-600">
                <Shield size={18} className="text-secondary-400 dark:text-gray-400" />
                <span className="text-secondary-900 dark:text-white capitalize">{userData?.role}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-gray-200 mb-2">
                Member Since
              </label>
              <div className="flex items-center space-x-2 p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg border border-secondary-200 dark:border-gray-600">
                <span className="text-secondary-900 dark:text-white">
                  {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Platform Preferences Section */}
      <Card>
        <Card.Header>
          <Card.Title>Platform Preferences</Card.Title>
          <Card.Description>Customize your experience on SkillForge</Card.Description>
        </Card.Header>

        <div className="space-y-6">
          {/* Theme Settings */}
          <div>
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
              <Moon className="w-4 h-4 mr-2" />
              Appearance
            </h3>
            <div className="flex gap-3">
              <button
                onClick={() => handleThemeChange('light')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  preferences.theme === 'light'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-secondary-200 dark:border-gray-600 hover:border-secondary-300 dark:hover:border-gray-500'
                }`}
              >
                <Sun className={`w-6 h-6 mx-auto mb-2 ${
                  preferences.theme === 'light' ? 'text-primary-600' : 'text-secondary-400 dark:text-gray-400'
                }`} />
                <p className="text-sm font-medium text-secondary-900 dark:text-white">Light</p>
              </button>
              <button
                onClick={() => handleThemeChange('dark')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  preferences.theme === 'dark'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-secondary-200 dark:border-gray-600 hover:border-secondary-300 dark:hover:border-gray-500'
                }`}
              >
                <Moon className={`w-6 h-6 mx-auto mb-2 ${
                  preferences.theme === 'dark' ? 'text-primary-600' : 'text-secondary-400 dark:text-gray-400'
                }`} />
                <p className="text-sm font-medium text-secondary-900 dark:text-white">Dark</p>
              </button>
            </div>
          </div>

          {/* Language Settings */}
          <div>
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
              <Globe className="w-4 h-4 mr-2" />
              Language
            </h3>
            <select
              value={preferences.language}
              onChange={handleLanguageChange}
              className="input w-full md:w-64"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="hi">हिन्दी</option>
            </select>
          </div>

          {/* Notification Settings */}
          <div>
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Email Notifications</p>
                  <p className="text-sm text-secondary-600 dark:text-gray-300">Receive updates via email</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.notifications.email}
                  onChange={() => handleNotificationToggle('email')}
                  className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Push Notifications</p>
                  <p className="text-sm text-secondary-600 dark:text-gray-300">Receive browser notifications</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.notifications.push}
                  onChange={() => handleNotificationToggle('push')}
                  className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Course Updates</p>
                  <p className="text-sm text-secondary-600 dark:text-gray-300">
                    {userData?.role === 'teacher' 
                      ? 'Notify when students enroll or complete lessons'
                      : 'Notify about new lessons and course changes'
                    }
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.notifications.courseUpdates}
                  onChange={() => handleNotificationToggle('courseUpdates')}
                  className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </label>

              {userData?.role === 'teacher' && (
                <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                  <div>
                    <p className="font-medium text-secondary-900 dark:text-white">New Enrollments</p>
                    <p className="text-sm text-secondary-600 dark:text-gray-300">Get notified when students enroll</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.notifications.newEnrollments}
                    onChange={() => handleNotificationToggle('newEnrollments')}
                    className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Privacy Settings */}
          <div>
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
              <Shield className="w-4 h-4 mr-2" />
              Privacy
            </h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Show Profile</p>
                  <p className="text-sm text-secondary-600 dark:text-gray-300">Make your profile visible to others</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.privacy.showProfile}
                  onChange={() => handlePrivacyToggle('showProfile')}
                  className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-secondary-100 dark:hover:bg-gray-600 transition-colors border border-secondary-200 dark:border-gray-600">
                <div>
                  <p className="font-medium text-secondary-900 dark:text-white">Show Progress</p>
                  <p className="text-sm text-secondary-600 dark:text-gray-300">Display your learning progress publicly</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.privacy.showProgress}
                  onChange={() => handlePrivacyToggle('showProgress')}
                  className="w-5 h-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
              </label>
            </div>
          </div>
        </div>

        <Card.Footer className="flex justify-end">
          <Button className="px-6">
            Save Preferences
          </Button>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Settings;
